import Phaser from 'phaser';
import { activePalette, type Palette } from '../util/palette';

/**
 * A draggable steering-wheel input. Grab anywhere on or near the ring, then
 * rotate around the center — the wheel's angle follows your finger 1:1, with
 * no cap (you can wind it past upside-down and keep going).
 *
 * Releasing springs the wheel back toward 0 via exponential decay.
 */
export class SteeringWheel {
  private scene: Phaser.Scene;
  private cx: number;
  private cy: number;
  private radius: number;
  private graphics: Phaser.GameObjects.Graphics;
  private hitArea: Phaser.GameObjects.Arc;
  private active = false;
  private pointerId: number | null = null;
  private prevPointerAngle = 0;
  private currentAngle = 0;
  private enabled = true;
  // Cached at construction — draw() runs per frame and activePalette() parses
  // the query string each call. A scene rebuild recreates the wheel anyway.
  private pal: Palette = activePalette();

  constructor(scene: Phaser.Scene, x: number, y: number, radius = 54) {
    this.scene = scene;
    this.cx = x;
    this.cy = y;
    this.radius = radius;
    // Larger hit area so the user can grab anywhere on the rim plus a generous
    // margin, instead of pixel-perfect ring hit testing.
    this.hitArea = scene.add
      .circle(x, y, radius * 1.35, 0xffffff, 0)
      .setDepth(69)
      .setInteractive({ useHandCursor: false });
    this.graphics = scene.add.graphics().setDepth(70);

    this.hitArea.on('pointerdown', this.onDown, this);
    scene.input.on('pointermove', this.onMove, this);
    scene.input.on('pointerup', this.onUp, this);
    scene.input.on('pointerupoutside', this.onUp, this);
    this.draw();
  }

  destroy(): void {
    this.scene.input.off('pointermove', this.onMove, this);
    this.scene.input.off('pointerup', this.onUp, this);
    this.scene.input.off('pointerupoutside', this.onUp, this);
    this.graphics.destroy();
    this.hitArea.destroy();
  }

  setEnabled(on: boolean): void {
    this.enabled = on;
  }

  isActive(): boolean {
    return this.active;
  }

  getAngle(): number {
    return this.currentAngle;
  }

  update(deltaMs: number): void {
    if (!this.active) {
      // Spring back toward 0. Normalize first so the path is the short way.
      if (this.currentAngle > Math.PI) this.currentAngle -= 2 * Math.PI;
      if (this.currentAngle < -Math.PI) this.currentAngle += 2 * Math.PI;
      const lerp = 1 - Math.exp((-deltaMs / 1000) * 9);
      this.currentAngle *= 1 - lerp;
      if (Math.abs(this.currentAngle) < 0.001) this.currentAngle = 0;
    }
    this.draw();
  }

  private onDown(pointer: Phaser.Input.Pointer): void {
    if (this.active || !this.enabled) return;
    this.active = true;
    this.pointerId = pointer.id;
    this.prevPointerAngle = Math.atan2(pointer.y - this.cy, pointer.x - this.cx);
  }

  private onMove(pointer: Phaser.Input.Pointer): void {
    if (!this.active || pointer.id !== this.pointerId) return;
    const a = Math.atan2(pointer.y - this.cy, pointer.x - this.cx);
    let delta = a - this.prevPointerAngle;
    while (delta > Math.PI) delta -= 2 * Math.PI;
    while (delta < -Math.PI) delta += 2 * Math.PI;
    this.currentAngle += delta;
    this.prevPointerAngle = a;
  }

  private onUp(pointer: Phaser.Input.Pointer): void {
    if (!this.active) return;
    if (this.pointerId !== null && pointer.id !== this.pointerId) return;
    this.active = false;
    this.pointerId = null;
  }

  private draw(): void {
    const g = this.graphics;
    g.clear();
    const accent = this.enabled ? this.pal.primary : this.pal.textLabel;
    // Recessed base: a deep disc with a brighter rim so the knob sits inside
    // a "well" rather than floating on the playfield.
    g.fillStyle(this.pal.surfaceDeep, 0.95);
    g.fillCircle(this.cx, this.cy, this.radius + 4);
    g.lineStyle(1, accent, 0.35);
    g.strokeCircle(this.cx, this.cy, this.radius + 4);
    // Knob body: chunky filled circle with a subtle highlight.
    g.fillStyle(this.pal.surface, 0.98);
    g.fillCircle(this.cx, this.cy, this.radius);
    g.lineStyle(2, accent, 0.85);
    g.strokeCircle(this.cx, this.cy, this.radius);
    // Top-left highlight: a small offset disc with low alpha for depth.
    g.fillStyle(0xffffff, 0.06);
    g.fillCircle(this.cx - this.radius * 0.3, this.cy - this.radius * 0.3, this.radius * 0.55);
    // Indicator notch at "12 o'clock" rotated by currentAngle. Wider than the
    // old single line so it reads as a physical groove.
    const ix = this.cx + Math.sin(this.currentAngle) * (this.radius - 8);
    const iy = this.cy - Math.cos(this.currentAngle) * (this.radius - 8);
    // Inner spoke (subtle).
    g.lineStyle(4, accent, 0.35);
    g.beginPath();
    g.moveTo(this.cx, this.cy);
    g.lineTo(ix, iy);
    g.strokePath();
    // Pill-shaped knob head at the end of the spoke.
    g.fillStyle(accent, 1);
    g.fillCircle(ix, iy, 7);
    g.lineStyle(1, this.pal.chipShadow, 0.6);
    g.strokeCircle(ix, iy, 7);
  }
}
